import { useUpdateAccountPropertiesMutation } from "@api/accounts";
import { useAppDispatch } from "@store";
import { setDialogId } from "@store/global-slice";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { Button } from "@/shared";
import { useCurrentAccount } from "../providers/current-account-provider";
import type { SubscriptionState } from "../types";
import { RadioSelect } from "./radio-select";

const types = [
  { title: "Trial", value: "trial" },
  { title: "Starter", value: "starter" },
  { title: "Pro", value: "pro" },
  { title: "Team", value: "team" },
];

const durations = [
  { title: "14 Days", value: 14 },
  { title: "1 Month", value: 30 },
  { title: "3 Months", value: 90 },
  { title: "6 Months", value: 180 },
  { title: "1 Year", value: 365 },
];

export const Subscription = () => {
  "use no memo";
  const dispatch = useAppDispatch();
  const { account, isDifferentFromCurrentAccount } = useCurrentAccount();
  const { setValue, watch, handleSubmit } = useForm<SubscriptionState>({
    defaultValues: {
      duration: 14,
      type: "",
    },
  });

  const [updateAccountProperties, { isLoading }] =
    useUpdateAccountPropertiesMutation();

  const onSubmit = async (data: SubscriptionState) => {
    try {
      await updateAccountProperties({
        type: "subscription",
        payload: data,
        id: account?._id as string,
      }).unwrap();
      if (isDifferentFromCurrentAccount) {
        toast.success(`${account?.title} subscription updated successfully`);
      } else {
        toast.success("Subscription updated successfully");
        window.location.reload();
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to update subscription");
    }

    dispatch(setDialogId({ id: null }));
  };

  const [type, duration] = watch(["type", "duration"]);

  return (
    <div>
      <span className="tw-mb-6 tw-block tw-font-sans tw-text-[13px]/normal tw-font-semibold tw-text-blue-base">
        Account subscription
      </span>
      <Type
        selected={type}
        setType={(value: string) => {
          setValue("type", value);
        }}
      />
      <Duration
        selected={duration}
        isTeam={type === "team"}
        setDuration={(value: number) => setValue("duration", value)}
      />
      <Button
        size="large"
        variant="secondary"
        title="Update subscription"
        disabled={!type}
        loading={isLoading}
        onClick={() => handleSubmit(onSubmit)()}
      />
    </div>
  );
};

const Type = ({
  selected,
  setType,
}: {
  selected: string | undefined;
  setType: (value: string) => void;
}) => {
  return (
    <div className="tw-mb-4 tw-w-full">
      <RadioSelect
        label="Type"
        selected={selected}
        options={types}
        setSelected={(value) => {
          setType(value as string);
        }}
      />
    </div>
  );
};

const Duration = ({
  selected,
  isTeam,
  setDuration,
}: {
  selected: number | undefined;
  isTeam: boolean;
  setDuration: (value: number) => void;
}) => {
  return (
    <div className="tw-relative tw-mb-6 tw-w-full">
      <RadioSelect
        label="Duration"
        selected={selected}
        options={durations}
        setSelected={(value) => {
          if (isTeam) return;
          setDuration(value as number);
        }}
      />
      {isTeam && (
        <span className="tw-absolute tw-left-0 tw-top-0 tw-block tw-h-full tw-w-full tw-cursor-not-allowed tw-bg-white/50"></span>
      )}
    </div>
  );
};
